import payfurl from '@payfurl/client';
import { useCallback, useEffect, useState } from 'react';
import { useDonorBlockProps } from '../DonorDetailProvider';
import { useDonorData } from '../libs/useDonorData';
import { usePayfurlContext } from './usePayfurlContext';
import Button from '2-core-elements/Buttons/Button';
import LoadingIndicator from '3-components/LoadingIndicator/LoadingIndicator';
import queryString from 'query-string';
import InlineMessage from '3-components/InlineMessage/InlineMessage';

interface PayfurlSuccessResponse {
    token: string;
}

type PayfurlConfig = {
    environment: 'sandbox' | 'production';
    cardProviderId?: string;
    publicKey: string;
}

type PayfurlOptions = {
    vault: boolean;
    cardProviderId?: string;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const verifyPayment = async(baseUrl: string, submissionId: string, paymentToken: string) => {
    const tokenEndpoint = `${baseUrl}/api/payfurl/pay`;

    // for local testing
    // const tokenEndpoint = `/assets/data/mock-payfurl-payment.json`;

    const payload = new FormData();
    payload.append('token', paymentToken);
    payload.append('submissionId', submissionId);
    payload.append('IsDonorUpdate', 'true');

    const response = await fetch(tokenEndpoint, {
        method: 'POST',
        body: payload,
    });

    if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData?.message;
        throw new Error(errorMessage);
    }

    return response.json();
};

interface PayfurlPaymentFormProps {
    environment: 'sandbox' | 'production';
    publicKey: string;
    cardProviderId: string;
}

const PayfurlPaymentForm = ({ environment, publicKey, cardProviderId }: PayfurlPaymentFormProps): JSX.Element => {
    const { baseURL, thankYouPageUrl } = useDonorBlockProps();
    const { donorData } = useDonorData();
    const { submissionId } = usePayfurlContext();

    const [paymentErrorMessage, setPaymentErrorMessage] = useState<string|null>(null);

    // verify payment state
    const [isVerifyPaymentLoading, setIsVerifyPaymentLoading] = useState<boolean>(false);
    const [isVerifyPaymentSuccess, setIsVerifyPaymentSuccess] = useState<boolean>(false);
    const [verifyPaymentErrorMessage, setVerifyPaymentErrorMessage] = useState<string|null>(null);

    const amount = donorData.amount || 1;
    const currency = donorData.currency || 'AUD';
    // always true because we only use payfurl to update monthly payment for donor update form.
    const isUseVault = true;

    const onPaymentSuccess = useCallback(async(response: PayfurlSuccessResponse): Promise<void> => {
        const { token } = response;

        setIsVerifyPaymentLoading(true);
        try {
            await verifyPayment(baseURL as string, submissionId, token);
            setIsVerifyPaymentSuccess(true);

            if (thankYouPageUrl) {
                const thankYouUrl = queryString.stringifyUrl({
                    url: thankYouPageUrl,
                    query: { donationKey: submissionId },
                });
                window.location.href = thankYouUrl;
            }
        } catch (error) {
            setVerifyPaymentErrorMessage(error instanceof Error ? error.message : 'Failed to verify payment.');
        } finally {
            setIsVerifyPaymentLoading(false);
        }
    }, [baseURL, submissionId, thankYouPageUrl]);

    const onPaymentFail = (): void => {
        console.error('Payment failed.');
        setPaymentErrorMessage('Payment failed. Please check your card details and try again or contact support.');
    };

    useEffect(() => {
        const payfurlOptions: PayfurlOptions = { vault: isUseVault };

        if (cardProviderId) {
            payfurlOptions.cardProviderId = cardProviderId;
        }

        payfurl.init(environment, publicKey).addDropIn('paymentContainer', amount, currency, payfurlOptions);

        payfurl.onSuccess(onPaymentSuccess);
        payfurl.onFailure(onPaymentFail);
    }, [environment, amount, currency, cardProviderId, publicKey, onPaymentSuccess, isUseVault]);

    if (paymentErrorMessage) {
        return (
            <InlineMessage status="error">
                {paymentErrorMessage}
            </InlineMessage>
        );
    }

    if (isVerifyPaymentLoading) {
        return (
            <LoadingIndicator
                loadingText={`Please wait, we are verifying your payment`}
            />
        );
    }

    if (isVerifyPaymentSuccess) {
        return (
            <InlineMessage status="success">
                The form has been submitted successfully.
            </InlineMessage>
        );
    }

    if (verifyPaymentErrorMessage) {
        return (
            <InlineMessage status="error">
                {verifyPaymentErrorMessage}
            </InlineMessage>
        );
    }

    if (!environment || !publicKey) {
        console.error('Missing required props for Payfurl Payment');
        return (
            <InlineMessage status="error">
                {`Missing required payment configuration`}
            </InlineMessage>
        );
    }

    return (
        <div className="fhf-payfurl-payment-wrapper">
            {!isVerifyPaymentSuccess && (
                <form method="POST" action="/">
                    <div id="paymentContainer" className="payfurl-payment-container" />
                    <div className="payfurl-submit-wrapper">
                        <Button type="submit" prefixIcon={'protection'} fullWidth={true}>Submit Payment</Button>
                    </div>
                </form>
            )}
        </div>
    );
};

const PayfurlPayment = (): JSX.Element => {
    const { baseURL } = useDonorBlockProps();

    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [config, setConfig]= useState<PayfurlConfig>();

    const resetError = (): void => {
        setIsError(false);
        setErrorMessage('');
    };

    useEffect(() => {
        const getPayfurlConfig = async(): Promise<void> => {
            try {
                const reqConfig = await fetch(`${baseURL}/api/payfurl/config`);
                // const reqConfig = await fetch(`/assets/data/mock-payfurl-config.json`);

                const payfurlConfig = await reqConfig.json();

                setConfig({
                    environment: payfurlConfig.Environment,
                    publicKey: payfurlConfig.PublicKey,
                    cardProviderId: payfurlConfig.CardProviderId || '',
                });

                resetError();
                setIsLoading(false);
            } catch (error) {
                setIsError(true);
                setErrorMessage(`Error loading Payfurl configuration`);
            }
        };

        getPayfurlConfig();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!isLoading && isError) {
        return (
            <InlineMessage status="error">
                {errorMessage}
            </InlineMessage>
        );
    }

    if (isLoading) {
        return (
            <LoadingIndicator
                loadingText={`Loading payment form`}
            />
        );
    }

    return (
        <PayfurlPaymentForm
            environment={config?.environment as 'sandbox' | 'production'}
            publicKey={config?.publicKey as string}
            cardProviderId={config?.cardProviderId as string}
        />
    );
};

export default PayfurlPayment;
